import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Route as RouteIcon, Plus, Pencil, Trash2, MapPin, ArrowRight } from "lucide-react";

export default function RouteList() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/routes")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/routes/${id}`);
    toast.success("Route supprimée");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
            <RouteIcon className="h-3.5 w-3.5" /> Navigation
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-50">Routes</h1>
          <p className="mt-1 text-sm text-slate-400">Créez et réutilisez vos itinéraires VFR.</p>
        </div>
        <Button data-testid="add-route-button" onClick={() => navigate("/routes/new")}
          className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          <Plus className="mr-1.5 h-4 w-4" /> Nouvelle route
        </Button>
      </div>

      {items === null ? (
        <div className="py-16 text-center text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center">
          <RouteIcon className="mx-auto mb-3 h-10 w-10 text-slate-600" />
          <p className="text-slate-400">Aucune route enregistrée.</p>
          <Button onClick={() => navigate("/routes/new")} variant="link" className="mt-1 text-cyan-400">Créer votre première route</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`route-card-${r.id}`}
              className="glass group rounded-2xl p-5 transition-all hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-base font-semibold text-slate-100">{r.name || "Sans nom"}</div>
                  <div className="mt-1 flex items-center gap-1.5 font-mono-data text-sm text-cyan-300">
                    <span>{r.departure || "?"}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                    <span>{r.arrival || "?"}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/routes/${r.id}`} data-testid={`edit-route-${r.id}`}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-cyan-300"><Pencil className="h-4 w-4" /></Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-route-${r.id}`}
                        className="rounded-lg p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette route ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{r.name} sera supprimée.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(r.id)} className="bg-rose-600 hover:bg-rose-500">Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-slate-800 pt-3 text-xs text-slate-400">
                <MapPin className="h-3.5 w-3.5 text-cyan-500/70" />
                {(r.legs?.length || 0)} branche{(r.legs?.length || 0) > 1 ? "s" : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
